import React from 'react';
import {
    makeStyles, tokens,
    Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
    Button, Text,
} from '@fluentui/react-components';
import { iconMap } from '../../theme/icons';
import { API_HOST } from '../../config.js';
import {
    PANEL_LOOKUPS, HANDLE_LOOKUPS, LEG_LOOKUPS,
    MIRROR_COLOR_LOOKUP, MDF_COLOR_LOOKUP, MDF_COLOR_TONE_LOOKUP,
    getLookupName,
} from './itemViewLookups';

const CloseIcon = iconMap.close;

const useStyles = makeStyles({
    surface: { maxWidth: '37.5rem' },
    grid: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalL },
    imageCol: { flex: '1 1 220px', minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    infoCol: { flex: '1 1 220px', minWidth: 0 },
    image: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
    noImage: { color: tokens.colorNeutralForeground3 },
    // Be numatytųjų <ul> taškelių — vien tekstinis "Etiketė: Reikšmė" sąrašas
    // taškelių nereikalauja (žr. /admin/bugs "is visu tokio tipo dialogu
    // nereikia, kad butu <ul>, tegul buna be taskiuku").
    infoList: { listStyleType: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
});

const fmtPrice = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? null : n.toFixed(2);
};
const fmtMm = (v) => (v !== undefined && v !== null && v !== '' ? `${v} mm` : null);

// Kiekvieno prekės tipo rodomų laukų sąrašas — TIKSLIAI nukopijuotas iš senosios
// (prieš-React) versijos atitinkamų peržiūros dialogų šablonų (žr.
// itemViewLookups.js pastabą dėl šaltinio failų). Kiekvienas įrašas — {label,
// value}; value===null/''/undefined eilutės tiesiog praleidžiamos, tad nereikia
// atskirai tikrinti kiekvieno lauko egzistavimo šaukimo vietoje.
const buildFieldRows = (item, itemType) => {
    switch (itemType) {
        case 'lmdp':
            return [
                { label: 'Pavadinimas', value: item.name },
                { label: 'Gamintojas', value: getLookupName(PANEL_LOOKUPS.supplier, item.supplier) },
                { label: 'Spalva', value: getLookupName(PANEL_LOOKUPS.color, item.color) },
                { label: 'Spalvos tonas', value: getLookupName(PANEL_LOOKUPS.color_tone, item.color_tone) },
                { label: 'Blizgumas', value: getLookupName(PANEL_LOOKUPS.gloss, item.gloss) },
                { label: 'Grublėtumas', value: getLookupName(PANEL_LOOKUPS.roughness, item.roughness) },
                { label: 'Raštas', value: getLookupName(PANEL_LOOKUPS.pattern, item.pattern) },
                { label: 'Medžiaga', value: 'LMDP' },
                { label: 'Plokštės storis', value: fmtMm(item.thickness) },
                { label: 'Kaina', value: fmtPrice(item.price) != null ? `${fmtPrice(item.price)} €` : null },
            ];
        case 'mdf':
            return [
                { label: 'Pavadinimas', value: item.name },
                { label: 'Gamintojas', value: 'UAB Termopalas' },
                { label: 'Spalva', value: MDF_COLOR_LOOKUP[item.color] || null },
                { label: 'Spalvos tonas', value: MDF_COLOR_TONE_LOOKUP[item.color_tone] || null },
                { label: 'Grublėtumas', value: 'Negrublėta' },
                { label: 'Raštas', value: 'Be rašto' },
                { label: 'Medžiaga', value: 'Dažytas MDF' },
            ];
        case 'mirror':
            return [
                { label: 'Pavadinimas', value: item.name },
                { label: 'Gamintojas', value: 'Sevroll' },
                { label: 'Spalva', value: MIRROR_COLOR_LOOKUP[item.color] || null },
                { label: 'Kaina', value: fmtPrice(item.price) != null ? `${fmtPrice(item.price)} €` : null },
            ];
        case 'handle':
            return [
                { label: 'Gamintojas', value: getLookupName(HANDLE_LOOKUPS.manufacturer, item.manufacturer) },
                { label: 'Medžiaga', value: getLookupName(HANDLE_LOOKUPS.material, item.material) },
                { label: 'Tekstūra', value: getLookupName(HANDLE_LOOKUPS.texture, item.texture) },
                { label: 'Spalva', value: getLookupName(HANDLE_LOOKUPS.color, item.color) },
                { label: 'Ilgis', value: fmtMm(item.length) },
                { label: 'Atstumas tarp skylių', value: fmtMm(item.distance) },
                { label: 'Išsikišimas', value: fmtMm(item.protrusion) },
                { label: 'Kaina', value: fmtPrice(item.price) != null ? `${fmtPrice(item.price)} €` : null },
            ];
        case 'leg':
            return [
                { label: 'Gamintojas', value: getLookupName(LEG_LOOKUPS.manufacturer, item.manufacturer) },
                { label: 'Medžiaga', value: getLookupName(LEG_LOOKUPS.material, item.material) },
                { label: 'Spalva', value: getLookupName(LEG_LOOKUPS.color, item.color) },
                { label: 'Reguliuojamas aukštis', value: getLookupName(LEG_LOOKUPS.adjustable_height, item.adjustable_height) },
                { label: 'Aukštis', value: fmtMm(item.height) },
                { label: 'Kaina', value: fmtPrice(item.price) != null ? `${fmtPrice(item.price)} €` : null },
            ];
        case 'filling':
            return [
                { label: 'Pavadinimas', value: item.name },
                { label: 'Filingų skaičius', value: item.filling_count },
                { label: 'Medžiaga', value: 'Frezuotas filingas' },
                { label: 'Aprašymas', value: item.description },
                { label: 'Kaina', value: fmtPrice(item.price_m2) != null ? `${fmtPrice(item.price_m2)} €` : null },
            ];
        default:
            // Nežinomas/nenurodytas tipas — saugus atsarginis variantas: TIK
            // pavadinimas ir kaina, joks neišverstas neapdorotų laukų dump'as
            // (žr. /admin/bugs — būtent tai buvo problema).
            return [
                { label: 'Pavadinimas', value: item.name },
                { label: 'Kaina', value: fmtPrice(item.price) != null ? `${fmtPrice(item.price)} €` : null },
            ];
    }
};

// itemType — 'lmdp'|'mdf'|'leg'|'mirror'|'handle'|'filling', nurodo kurį lauko
// sąrašą (buildFieldRows) ir kurį paveikslėlio kelią naudoti. panelType (0/1/2)
// paliktas kaip ATGALINIO SUDERINAMUMO alias tiems retiems kvietimo taškams,
// kurie dar jo nepakeitė į itemType (0→lmdp, 1→mdf, 2→leg) — visos šios
// sesijos žinomos vietos jau perduoda itemType tiesiogiai.
// imageUrl — pasirenkamas, JAU IŠSPRĘSTAS paveikslėlio URL, naudojamas kai
// šaukiantysis jau žino teisingą kelią (pvz. jei norima permaišyti/laikinai
// perrašyti numatytąją logiką).
const ItemViewDialog = ({ open, onClose, item, itemType, panelType, imageUrl }) => {
    const s = useStyles();
    if (!item) return null;

    const resolvedType = itemType || { 0: 'lmdp', 1: 'mdf', 2: 'leg' }[panelType] || null;

    const getImagePath = () => {
        if (imageUrl) return imageUrl;
        const imageName = item.image_1 || item.image;
        if (!imageName) return null;
        if (imageName.startsWith('http')) return imageName;
        if (resolvedType === 'lmdp') return `/lmdp/${imageName}`;
        if (resolvedType === 'mdf') return `/mdf/${imageName}`;
        if (['leg', 'mirror', 'handle', 'filling'].includes(resolvedType)) return `${API_HOST}/uploads/${imageName}`;
        return null;
    };

    const imagePath = getImagePath();
    const fieldRows = buildFieldRows(item, resolvedType).filter(
        (r) => r.value !== null && r.value !== undefined && r.value !== ''
    );

    return (
        <Dialog open={open} onOpenChange={(_e, data) => { if (!data.open) onClose(); }}>
            <DialogSurface id="dialog-item-view" className={s.surface}>
                <DialogBody>
                    <DialogTitle action={<Button appearance="subtle" icon={<CloseIcon />} aria-label="Uždaryti" onClick={onClose} />}>
                        {item.name || item.code}
                    </DialogTitle>
                    <DialogContent>
                        <div className={s.grid}>
                            <div className={s.imageCol}>
                                {imagePath ? (
                                    <img src={imagePath} alt={item.name || item.code} className={s.image} />
                                ) : (
                                    <Text size={200} className={s.noImage}>Paveikslėlis neprieinamas</Text>
                                )}
                            </div>
                            <div className={s.infoCol}>
                                <h5>{item.code}</h5>
                                <ul className={s.infoList}>
                                    {fieldRows.map((r) => (
                                        <li key={r.label}>{r.label}: <strong>{r.value}</strong></li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button size="large" appearance="outline" onClick={onClose}>Uždaryti</Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default ItemViewDialog;
